import { html, css } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { BaseElement } from '../base/base-element.js';

/**
 * <alphatab-viewer> — Encapsulates the alphaTab container, loading overlay,
 * and cursor styling. Does NOT manage the alphaTab API lifecycle; conductor.ts
 * and player.ts retain that responsibility.
 *
 * Usage:
 *   <alphatab-viewer id="conductor-viewer" loading>
 *       <count-in-overlay id="count-in-overlay"></count-in-overlay>
 *   </alphatab-viewer>
 */
@customElement('alphatab-viewer')
export class AlphaTabViewer extends BaseElement {

    /** Show/hide loading overlay */
    @property({ type: Boolean, reflect: true })
    loading = false;

    /** Progress bar width 0–100 */
    @property({ type: Number })
    loadingProgress = 0;

    /** Loading text shown in overlay */
    @property({ type: String })
    loadingMessage = 'Loading music...';

    /** Hides beat cursor via CSS :host([playing]) */
    @property({ type: Boolean, reflect: true })
    playing = false;

    /** Hides cursor bar + beat cursor via CSS :host([looping]) */
    @property({ type: Boolean, reflect: true })
    looping = false;

    /** Hides alphaTab's built-in cursors so practice mode can draw its own. */
    @property({ type: Boolean, reflect: true, attribute: 'practice-mode' })
    practiceMode = false;

    /** Whether loop drag handles are visible */
    @property({ type: Boolean })
    loopHandlesVisible = false;

    /** Internal reference to the alphaTab render target */
    @query('.container')
    private _container!: HTMLDivElement;

    @query('.loop-handle-start')
    private _startHandle!: HTMLDivElement;

    @query('.loop-handle-end')
    private _endHandle!: HTMLDivElement;

    /** Public accessor for the container element (passed to alphatab.initializeAlphaTab) */
    get container(): HTMLDivElement {
        return this._container;
    }

    private _resizeTimeout: number | null = null;
    private _isDragging = false;
    private _dragHandle: 'start' | 'end' | null = null;
    private _scrollAnimFrame: number | null = null;
    private _lastPointerX = 0;
    private _lastPointerY = 0;
    /**
     * Pointer X minus the loop boundary X, saved when a drag starts. The grip
     * sits on the bracket's outer bar, about one handle-width from the
     * boundary, so the drag must carry this offset or the loop edge would land
     * away from where the bracket is drawn.
     */
    private _grabOffsetX = 0;
    /**
     * Where the pointer went down, and whether it has since moved past the
     * dead zone. A press that stays inside the dead zone is a tap, not a drag,
     * and must not change the loop: the grab offset makes a tap drop exactly
     * on the handle's boundary line, and conductor resolves that line to the
     * neighbouring beat, which would nudge the loop edge outward.
     */
    private _dragStartClientX = 0;
    private _dragStartClientY = 0;
    private _dragMoved = false;
    private static readonly DRAG_DEAD_ZONE_PX = 4;
    /** Cached surface coordinates for scroll-aware repositioning */
    private _handleSurfacePositions: {
        startX: number; startY: number; startHeight: number;
        endX: number; endY: number; endHeight: number;
    } | null = null;

    static styles = [
        BaseElement.styles,
        css`
            /* alphaTab music font — must be declared inside shadow DOM
               so that .at glyph elements can resolve font-family: 'alphaTab' */
            @font-face {
                font-display: block;
                font-family: 'alphaTab';
                src: url("/museiq/font/Bravura.woff2") format('woff2'),
                     url("/museiq/font/Bravura.woff") format('woff'),
                     url("/museiq/font/Bravura.otf") format('opentype');
                font-weight: normal;
                font-style: normal;
            }

            /* alphaTab shared surface rules */
            .at-surface * {
                cursor: default;
                vertical-align: top;
                overflow: visible;
            }

            .at-surface-svg text {
                dominant-baseline: alphabetic;
                white-space: pre;
            }

            /* alphaTab music glyph font application */
            .at-surface.at .at {
                font-family: 'alphaTab';
                speak: none;
                font-style: normal;
                font-weight: normal;
                font-variant: normal;
                text-transform: none;
                line-height: 1;
                -webkit-font-smoothing: antialiased;
                -moz-osx-font-smoothing: grayscale;
                font-size: 36px;
                overflow: visible !important;
            }

            :host {
                position: relative;
                flex: 1;
                min-height: 0;
                display: flex;
                flex-direction: column;
            }

            /* alphaTab render target */
            .container {
                position: relative;
                width: 100%;
                min-height: 200px;
                flex: 1;
                background-color: #ffffff;
                border-radius: 0.5rem;
                overflow-y: auto;
                overflow-x: hidden;
                box-shadow: 0 4px 20px var(--shadow);
            }

            .container .at-surface-svg {
                overflow-y: auto;
                overflow-x: hidden;
            }

            /* Cursor styles */
            .at-cursor-bar {
                background: rgba(234, 179, 8, 0.3) !important;
            }

            .at-cursor-beat {
                background: var(--primary-color) !important;
            }

            :host([playing]) .at-cursor-beat {
                visibility: hidden !important;
            }

            :host([looping]) .at-cursor-bar {
                visibility: hidden !important;
            }

            :host([looping]) .at-cursor-beat {
                visibility: hidden !important;
            }

            /* Practice mode draws its own cursor (.practice-cursor below);
               hide alphaTab's built-ins. visibility:hidden preserves layout
               so cursor-manager's getBoundingClientRect() reads still work. */
            :host([practice-mode]) .at-cursor-bar,
            :host([practice-mode]) .at-cursor-beat {
                visibility: hidden !important;
            }

            /* Practice mode custom cursor — appended as sibling of .at-cursor-bar
               by practice-mode/cursor.ts. Sits above the SVG (z-index:2),
               beneath loop handles (z-index:5). */
            .practice-cursor {
                position: absolute;
                pointer-events: none;
                box-sizing: border-box;
                background: rgba(234, 179, 8, 0.12);
                border: 1px solid rgba(234, 179, 8, 0.55);
                border-radius: 3px;
                opacity: 0;
                transition: opacity 80ms ease-out;
                z-index: 2;
            }
            .practice-cursor.visible { opacity: 1; }
            .practice-cursor.pulsing { animation: practice-gate-pulse 1.2s ease-in-out infinite; }
            @keyframes practice-gate-pulse {
                0%, 100% { opacity: 1; }
                50%      { opacity: 0.55; }
            }

            /* Dynamics warning badge — appended by dynamics-toast.ts, anchored above
               the offending beat's staff system. Above the practice cursor (z-index:2)
               but below the loop handles (z-index:5). */
            .dynamics-toast {
                position: absolute;
                pointer-events: none;
                white-space: nowrap;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
                font-size: 16px;
                font-weight: 700;
                letter-spacing: 0.02em;
                color: #fff;
                padding: 8px 14px;
                border-radius: 8px;
                box-shadow: 0 4px 14px rgba(0, 0, 0, 0.4);
                opacity: 0;
                visibility: hidden;
                transition: opacity 120ms ease-out;
                z-index: 4;
            }
            /* Cartoon cue. Larger than the body text so it stays legible in peripheral
               vision while the player is reading notes. */
            .dynamics-toast .dyn-emoji {
                font-size: 24px;
                line-height: 1;
                margin-right: 7px;
                vertical-align: -4px;
            }
            /* The target marking, set like the notation it refers to. */
            .dynamics-toast .dyn-mark {
                font-style: italic;
                font-weight: 800;
            }
            .dynamics-toast.too-loud { background: var(--error-color, #ef4444); }
            .dynamics-toast.too-soft { background: #3498db; }
            .dynamics-toast.visible { opacity: 1; visibility: visible; }
            /* Laid out but transparent so offsetWidth/Height can be measured
               before positioning, without a visible flash at the old location. */
            .dynamics-toast.measuring { visibility: hidden; opacity: 0; }

            .at-selection {
                background: rgba(37, 99, 235, 0.15) !important;
            }

            .at-highlight *:not(text):not(tspan) {
                fill: var(--primary-color) !important;
            }

            /* Loop drag handles: start is a rounded "[", end is a rounded "]".
               Both open toward the loop, so the open tips sit on the loop
               boundary and the bracket itself sits just outside the loop.
               Width must match LOOP_HANDLE_W in the class below. */
            .loop-handle-start,
            .loop-handle-end {
                position: absolute;
                box-sizing: border-box;
                width: 14px;
                border: 3px solid var(--primary-color, #2563eb);
                background: transparent;
                z-index: 5;
                display: none;
                touch-action: none;
                cursor: ew-resize;
                opacity: 0.8;
                transition: opacity 0.15s;
            }

            .loop-handle-start {
                border-right: none;
                border-radius: 12px 0 0 12px;
            }

            .loop-handle-end {
                border-left: none;
                border-radius: 0 12px 12px 0;
            }

            .loop-handle-start::before,
            .loop-handle-end::before {
                /* Expand the touch target to about 44px wide */
                content: '';
                position: absolute;
                top: -4px;
                bottom: -4px;
                left: -15px;
                right: -15px;
            }

            /* Grip pill on the outer vertical bar */
            .loop-handle-start::after,
            .loop-handle-end::after {
                content: '';
                position: absolute;
                top: 50%;
                width: 8px;
                height: 36px;
                max-height: 60%;
                transform: translateY(-50%);
                border-radius: 4px;
                background: var(--primary-color, #2563eb);
            }

            .loop-handle-start::after {
                left: -5.5px;
            }

            .loop-handle-end::after {
                right: -5.5px;
            }

            .loop-handle-start:hover,
            .loop-handle-end:hover {
                opacity: 1;
            }

            .loop-handle-start.dragging,
            .loop-handle-end.dragging {
                opacity: 1;
                border-color: var(--primary-dark, #1e40af);
            }

            .loop-handle-start.dragging::after,
            .loop-handle-end.dragging::after {
                background: var(--primary-dark, #1e40af);
            }

            :host([playing]) .loop-handle-start,
            :host([playing]) .loop-handle-end {
                cursor: default;
                opacity: 0.5;
            }

            /* Loading overlay */
            .loading-overlay {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                background-color: var(--surface);
                color: var(--text-primary);
                z-index: 10;
            }

            :host(:not([loading])) .loading-overlay {
                display: none;
            }

            .loading-text {
                font-size: 1rem;
                margin-bottom: 0.5rem;
            }

            .loading-progress {
                width: 200px;
                height: 4px;
                background-color: var(--surface-light);
                border-radius: 2px;
                overflow: hidden;
            }

            .loading-progress-bar {
                height: 100%;
                background-color: var(--primary-color);
                transition: width 0.3s ease;
            }
        `
    ];

    connectedCallback(): void {
        super.connectedCallback();
        window.addEventListener('resize', this._handleResize);
        window.addEventListener('orientationchange', this._handleOrientationChange);
    }

    protected firstUpdated(): void {
        // Listen for container scroll to reposition loop handles
        this._container?.addEventListener('scroll', this._handleContainerScroll);
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        window.removeEventListener('resize', this._handleResize);
        window.removeEventListener('orientationchange', this._handleOrientationChange);
        this._container?.removeEventListener('scroll', this._handleContainerScroll);
        if (this._resizeTimeout !== null) {
            clearTimeout(this._resizeTimeout);
        }
    }

    /** Clear inline height and dispatch resize for alphaTab relayout */
    recalculateLayout(): void {
        if (this._container) {
            this._container.style.height = '';
        }
        window.dispatchEvent(new Event('resize'));
    }

    private _handleResize = (): void => {
        if (this._resizeTimeout !== null) {
            clearTimeout(this._resizeTimeout);
        }
        this._resizeTimeout = window.setTimeout(() => {
            this.recalculateLayout();
        }, 300);
    };

    private _handleOrientationChange = (): void => {
        setTimeout(() => {
            this.recalculateLayout();
        }, 100);
    };

    /**
     * Update loop handle positions from alphaTab surface bounds.
     * Coordinates are in alphaTab surface space; scroll offset is subtracted
     * so handles align with the visible viewport.
     */
    updateLoopHandlePositions(
        startX: number, startY: number, startHeight: number,
        endX: number, endY: number, endHeight: number
    ): void {
        this._handleSurfacePositions = { startX, startY, startHeight, endX, endY, endHeight };
        this._applyHandlePositions();
    }

    private _applyHandlePositions(): void {
        if (!this._handleSurfacePositions) return;
        const { startX, startY, startHeight, endX, endY, endHeight } = this._handleSurfacePositions;
        const scrollTop = this._container?.scrollTop ?? 0;
        const display = this.loopHandlesVisible ? 'block' : 'none';
        if (this._startHandle) {
            this._startHandle.style.left = `${this._handleLeft('start', startX)}px`;
            this._startHandle.style.top = `${startY - scrollTop}px`;
            this._startHandle.style.height = `${startHeight}px`;
            this._startHandle.style.display = display;
        }
        if (this._endHandle) {
            this._endHandle.style.left = `${this._handleLeft('end', endX)}px`;
            this._endHandle.style.top = `${endY - scrollTop}px`;
            this._endHandle.style.height = `${endHeight}px`;
            this._endHandle.style.display = display;
        }
    }

    /**
     * CSS `left` for a handle whose loop boundary is at `boundaryX`. Each
     * bracket sits outside the loop with its open tips on the boundary:
     * "[" ends at the boundary, "]" starts at it.
     */
    private _handleLeft(handle: 'start' | 'end', boundaryX: number): number {
        return handle === 'start' ? boundaryX - AlphaTabViewer.LOOP_HANDLE_W : boundaryX;
    }

    /** Must match the `width` of .loop-handle-start / .loop-handle-end. */
    private static readonly LOOP_HANDLE_W = 14;

    private _handleContainerScroll = (): void => {
        if (this.loopHandlesVisible && this._handleSurfacePositions) {
            this._applyHandlePositions();
        }
    };

    /** Start auto-scrolling the container during handle drag */
    private _startAutoScroll(speed: number): void {
        // Already scrolling — just update speed via closure below
        if (this._scrollAnimFrame !== null) {
            cancelAnimationFrame(this._scrollAnimFrame);
        }

        const tick = () => {
            if (!this._isDragging || !this._dragHandle) {
                this._scrollAnimFrame = null;
                return;
            }

            this._container.scrollTop += speed;

            // Re-dispatch loop-drag-move with updated alphaTabY (scroll changed)
            const containerRect = this._container.getBoundingClientRect();
            const alphaTabX = this._lastPointerX - containerRect.left - this._grabOffsetX;
            const alphaTabY = this._lastPointerY - containerRect.top + this._container.scrollTop;

            // Update handle's horizontal position to track pointer
            const el = this._dragHandle === 'start' ? this._startHandle : this._endHandle;
            if (el) {
                el.style.left = `${this._handleLeft(this._dragHandle, alphaTabX)}px`;
            }

            this.dispatchEvent(new CustomEvent('loop-drag-move', {
                bubbles: true, composed: true,
                detail: { handle: this._dragHandle, alphaTabX, alphaTabY }
            }));

            this._scrollAnimFrame = requestAnimationFrame(tick);
        };

        this._scrollAnimFrame = requestAnimationFrame(tick);
    }

    /** Stop auto-scrolling */
    private _stopAutoScroll(): void {
        if (this._scrollAnimFrame !== null) {
            cancelAnimationFrame(this._scrollAnimFrame);
            this._scrollAnimFrame = null;
        }
    }

    /** Show loop handles */
    showLoopHandles(): void {
        this.loopHandlesVisible = true;
        if (this._startHandle) this._startHandle.style.display = 'block';
        if (this._endHandle) this._endHandle.style.display = 'block';
    }

    /** Hide loop handles */
    hideLoopHandles(): void {
        this.loopHandlesVisible = false;
        this._handleSurfacePositions = null;
        if (this._startHandle) this._startHandle.style.display = 'none';
        if (this._endHandle) this._endHandle.style.display = 'none';
    }

    private _onHandlePointerDown(e: PointerEvent, handle: 'start' | 'end'): void {
        if (this.playing) return;
        e.preventDefault();
        e.stopPropagation();

        this._isDragging = true;
        this._dragHandle = handle;

        // Remember where on the handle the user grabbed, relative to the loop
        // boundary, so the boundary keeps that offset from the pointer.
        const boundaryX = handle === 'start'
            ? this._handleSurfacePositions?.startX
            : this._handleSurfacePositions?.endX;
        const pointerX = e.clientX - this._container.getBoundingClientRect().left;
        this._grabOffsetX = boundaryX !== undefined ? pointerX - boundaryX : 0;
        this._dragStartClientX = e.clientX;
        this._dragStartClientY = e.clientY;
        this._dragMoved = false;

        const el = handle === 'start' ? this._startHandle : this._endHandle;
        el.setPointerCapture(e.pointerId);
        el.classList.add('dragging');

        this.dispatchEvent(new CustomEvent('loop-drag-start', {
            bubbles: true, composed: true,
            detail: { handle }
        }));
    }

    private _onHandlePointerMove(e: PointerEvent): void {
        if (!this._isDragging || !this._dragHandle) return;
        e.preventDefault();

        // Store pointer position for auto-scroll re-dispatch
        this._lastPointerX = e.clientX;
        this._lastPointerY = e.clientY;

        // Ignore jitter until the pointer leaves the dead zone (see _dragMoved).
        if (!this._dragMoved) {
            const dx = Math.abs(e.clientX - this._dragStartClientX);
            const dy = Math.abs(e.clientY - this._dragStartClientY);
            if (dx < AlphaTabViewer.DRAG_DEAD_ZONE_PX && dy < AlphaTabViewer.DRAG_DEAD_ZONE_PX) return;
            this._dragMoved = true;
        }

        // Move the handle element to follow the pointer in real-time
        const el = this._dragHandle === 'start' ? this._startHandle : this._endHandle;
        const containerRect = this._container.getBoundingClientRect();
        const alphaTabX = e.clientX - containerRect.left - this._grabOffsetX;
        if (el) {
            el.style.left = `${this._handleLeft(this._dragHandle, alphaTabX)}px`;
        }

        // Auto-scroll when pointer is near top/bottom edge of container
        const EDGE_ZONE = 40;
        const distFromBottom = containerRect.bottom - e.clientY;
        const distFromTop = e.clientY - containerRect.top;

        if (distFromBottom < EDGE_ZONE && distFromBottom >= 0) {
            // Near bottom edge — scroll down, speed proportional to proximity
            const speed = Math.ceil((EDGE_ZONE - distFromBottom) / EDGE_ZONE * 8);
            this._startAutoScroll(speed);
        } else if (distFromTop < EDGE_ZONE && distFromTop >= 0) {
            // Near top edge — scroll up
            const speed = -Math.ceil((EDGE_ZONE - distFromTop) / EDGE_ZONE * 8);
            this._startAutoScroll(speed);
        } else {
            this._stopAutoScroll();
        }

        const alphaTabY = e.clientY - containerRect.top + this._container.scrollTop;

        this.dispatchEvent(new CustomEvent('loop-drag-move', {
            bubbles: true, composed: true,
            detail: {
                handle: this._dragHandle,
                alphaTabX,
                alphaTabY
            }
        }));
    }

    private _onHandlePointerUp(e: PointerEvent): void {
        if (!this._isDragging || !this._dragHandle) return;

        this._stopAutoScroll();

        const el = this._dragHandle === 'start' ? this._startHandle : this._endHandle;
        el.releasePointerCapture(e.pointerId);
        el.classList.remove('dragging');

        // A tap (never left the dead zone) leaves the loop as it was.
        if (!this._dragMoved) {
            this._grabOffsetX = 0;
            this._isDragging = false;
            this._dragHandle = null;
            return;
        }

        const containerRect = this._container.getBoundingClientRect();
        const alphaTabX = e.clientX - containerRect.left - this._grabOffsetX;
        const alphaTabY = e.clientY - containerRect.top + this._container.scrollTop;
        this._grabOffsetX = 0;

        this.dispatchEvent(new CustomEvent('loop-drag-end', {
            bubbles: true, composed: true,
            detail: {
                handle: this._dragHandle,
                alphaTabX,
                alphaTabY
            }
        }));

        this._isDragging = false;
        this._dragHandle = null;
    }

    render() {
        return html`
            <div class="container">
                <div class="loading-overlay">
                    <div class="loading-text">${this.loadingMessage}</div>
                    <div class="loading-progress">
                        <div class="loading-progress-bar" style="width: ${this.loadingProgress}%"></div>
                    </div>
                </div>
            </div>
            <div class="loop-handle-start"
                @pointerdown=${(e: PointerEvent) => this._onHandlePointerDown(e, 'start')}
                @pointermove=${this._onHandlePointerMove}
                @pointerup=${this._onHandlePointerUp}
                @pointercancel=${this._onHandlePointerUp}
            ></div>
            <div class="loop-handle-end"
                @pointerdown=${(e: PointerEvent) => this._onHandlePointerDown(e, 'end')}
                @pointermove=${this._onHandlePointerMove}
                @pointerup=${this._onHandlePointerUp}
                @pointercancel=${this._onHandlePointerUp}
            ></div>
            <slot></slot>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'alphatab-viewer': AlphaTabViewer;
    }
}
